import { create } from 'zustand';
import { useEffect, useRef, useCallback } from 'react';
import { useUrlValue } from './urlStateStore';
import { useSearchParams } from 'react-router-dom';
import type { SocialPlatform } from '../components/icons/SocialIcon';

// Accounts page state
interface AccountsState {
  selectedBrand: string;
  activeTab: string; // 'all' or a platform ID
  
  // Actions
  setSelectedBrand: (brandId: string) => void;
  setActiveTab: (tab: string) => void;
}

// Create the store with default values
export const useAccountsStore = create<AccountsState>((set, get) => {
  return {
    selectedBrand: 'all',
    activeTab: 'all',
    
    // Actions
    setSelectedBrand: (selectedBrand) => set((state) => {
      if (state.selectedBrand === selectedBrand) return state;
      return { selectedBrand };
    }),
    setActiveTab: (activeTab) => set((state) => {
      if (state.activeTab === activeTab) return state;
      return { activeTab };
    })
  };
});

// Hook to use accounts state with URL synchronization
export function useAccountsUrlState() {
  // Get the raw state and actions from Zustand
  const store = useAccountsStore();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Create refs to track initial values and prevent circular updates
  const isInitialMount = useRef(true);
  const updatingFromStore = useRef(false);
  const storeRef = useRef(store);
  
  // Update store ref
  useEffect(() => {
    storeRef.current = store;
  }, [store]);
  
  // Clean up URL by removing unwanted parameters
  useEffect(() => {
    let shouldUpdateParams = false;
    const newParams = new URLSearchParams(searchParams);
    
    // Remove 'platform' parameter if it exists (belongs to Analytics page)
    if (newParams.has('platform')) {
      newParams.delete('platform');
      shouldUpdateParams = true;
    }
    
    // Remove 'view' parameter if it exists (belongs to Calendar page)
    if (newParams.has('view')) {
      newParams.delete('view');
      shouldUpdateParams = true;
    }
    
    // Remove 'status' parameter if it exists (belongs to Calendar page)
    if (newParams.has('status')) {
      newParams.delete('status');
      shouldUpdateParams = true;
    }
    
    // Remove 'dateRange' parameter if it exists (belongs to Calendar/Analytics pages)
    if (newParams.has('dateRange')) {
      newParams.delete('dateRange');
      shouldUpdateParams = true;
    }
    
    if (shouldUpdateParams) {
      setSearchParams(newParams);
    }
  }, [searchParams, setSearchParams]);
  
  // Connect state values to URL parameters with stable options
  const tabOptions = useRef({ skipSync: false }).current;
  const brandOptions = useRef({ skipSync: false }).current;
  
  // Connect state values to URL parameters
  const [activeTab, setActiveTab] = useUrlValue<string>(
    'tab', 
    store.activeTab,
    tabOptions
  );
  
  const [selectedBrand, setSelectedBrand] = useUrlValue<string>(
    'brand',
    store.selectedBrand,
    brandOptions
  );
  
  // On initial mount, sync the store with URL values (URL has priority)
  useEffect(() => {
    if (isInitialMount.current) {
      // Only update the store if URL values are different from defaults
      const currentStore = storeRef.current;
      
      if (activeTab !== currentStore.activeTab) currentStore.setActiveTab(activeTab);
      if (selectedBrand !== currentStore.selectedBrand) currentStore.setSelectedBrand(selectedBrand);
      
      isInitialMount.current = false;
    }
  }, []); // Empty dependency array ensures this runs only once
  
  // Create wrapper functions for setters that update both URL and store
  const setActiveTabWrapper = useCallback((newTab: string) => {
    // Skip if the value hasn't changed
    if (newTab === activeTab) return;
    
    // Update URL first
    setActiveTab(newTab);
    
    // Then update store, but mark that we're updating from a user action
    updatingFromStore.current = true;
    storeRef.current.setActiveTab(newTab);
    updatingFromStore.current = false;
  }, [activeTab, setActiveTab]);
  
  const setSelectedBrandWrapper = useCallback((newBrand: string) => {
    if (newBrand === selectedBrand) return;
    
    setSelectedBrand(newBrand);
    updatingFromStore.current = true;
    storeRef.current.setSelectedBrand(newBrand);
    updatingFromStore.current = false;
  }, [selectedBrand, setSelectedBrand]);
  
  // Keep Zustand store in sync with URL changes, but only if it's coming from the URL
  // and not from our wrapper functions
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && activeTab !== storeRef.current.activeTab) {
      storeRef.current.setActiveTab(activeTab);
    }
  }, [activeTab]);
  
  useEffect(() => {
    if (!isInitialMount.current && !updatingFromStore.current && selectedBrand !== storeRef.current.selectedBrand) {
      storeRef.current.setSelectedBrand(selectedBrand);
    }
  }, [selectedBrand]);
  
  // Return URL-synced values and our wrapped setters
  return {
    // URL synced values
    activeTab,
    selectedBrand,
    
    // URL synced setters (using our wrapper functions)
    setActiveTab: setActiveTabWrapper,
    setSelectedBrand: setSelectedBrandWrapper
  };
} 